import type IGameSettings from 'types/models/IGameSettings';
import { SEARCH_FILTERS } from '../constants/searchConstants';
import { categorizeGame, getNormalizedGameFeatures, normalizeString } from './gameSearchHelpers';

/**
 * Helper function for normalizing filter option IDs
 */
const getFormattedSelectedOptions = (selectedOptions: Set<string>) =>
  Array.from(selectedOptions, (option) => normalizeString(option.replace(/_/g, ' ')));

/**
 * Functions to check if a game matches selected filter options for each category
 */
const matchesLines = (lines: number[] | undefined, selectedOptions: Set<string>): boolean => {
  if (!lines || lines.length === 0) return false;

  return lines.some((line) =>
    Array.from(selectedOptions).some((optionId) => normalizeString(optionId) === line.toString()),
  );
};

const matchesFeatures = (gameFeatures: string[] | undefined, selectedOptions: Set<string>): boolean => {
  if (!gameFeatures || gameFeatures.length === 0) return false;

  const normalizedGameFeatures = getNormalizedGameFeatures(gameFeatures);
  const normalizedSelectedIds = new Set(Array.from(selectedOptions, normalizeString));

  return Array.from(normalizedSelectedIds).every((optionId) => normalizedGameFeatures.has(optionId));
};

const matchesMaxWin = (game: IGameSettings, selectedOptions: Set<string>): boolean => {
  const maxWinBucket = categorizeGame(game, SEARCH_FILTERS.MAX_WIN);
  if (!maxWinBucket) return false;

  return selectedOptions.has(maxWinBucket);
};

const matchesPayout = (paymode: string | undefined, selectedOptions: Set<string>): boolean => {
  if (!paymode) return false;

  const formattedOptions = getFormattedSelectedOptions(selectedOptions);
  return formattedOptions.some((optionId) => optionId === normalizeString(paymode));
};

const matchesJackpot = (game: IGameSettings, selectedOptions: Set<string>): boolean => {
  const jackpotBucket = categorizeGame(game, SEARCH_FILTERS.JACKPOT);
  if (!jackpotBucket) return false;

  const formattedOptions = getFormattedSelectedOptions(selectedOptions);
  return formattedOptions.some((optionId) => optionId === normalizeString(jackpotBucket));
};

const matchesCampaigns = (
  gameKey: string,
  selectedOptions: Set<string>,
  campaignGameKeysByType: Record<string, Set<string>>,
): boolean => {
  const normalizedSelectedIds = new Set(Array.from(selectedOptions, normalizeString));

  return Array.from(normalizedSelectedIds).every((optionId) => campaignGameKeysByType[optionId].has(gameKey));
};

/**
 * Filter games by multiple criteria with AND logic between categories.
 */
export const filterGames = (
  games: IGameSettings[],
  selectedFilters: Record<string, Set<string>>,
  campaignGameKeysByType: Record<string, Set<string>>,
): IGameSettings[] => {
  return games.filter((game) => {
    for (const [filterId, selectedOptions] of Object.entries(selectedFilters)) {
      switch (filterId) {
        case SEARCH_FILTERS.LINES:
          if (!matchesLines(game.lines, selectedOptions)) return false;
          break;
        case SEARCH_FILTERS.FEATURES:
          if (!matchesFeatures(game.gameFeatures, selectedOptions)) return false;
          break;
        case SEARCH_FILTERS.MAX_WIN:
          if (!matchesMaxWin(game, selectedOptions)) return false;
          break;
        case SEARCH_FILTERS.PAYOUT:
          if (!matchesPayout(game.paymode, selectedOptions)) return false;
          break;
        case SEARCH_FILTERS.JACKPOT:
          if (!matchesJackpot(game, selectedOptions)) return false;
          break;
        case SEARCH_FILTERS.CAMPAIGNS:
          if (!matchesCampaigns(game.gameKey, selectedOptions, campaignGameKeysByType)) return false;
          break;
        default:
          break;
      }
    }

    return true;
  });
};
